if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Clear the "Please Choose Size" notice + refresh the buy-now gate as soon as a
        // size is picked. Delegated on document because <variant-selects> is replaced
        // wholesale on variant change.
        this.onDocumentChange = (event) => {
          if (!this.isConnected) return;
          const input = event.target;
          if (!input || input.type !== 'radio') return;
          const fieldset = this.sizeFieldset;
          if (fieldset && fieldset.contains(input)) this.clearSizeError();
          this.updateSizeGate();
        };
        document.addEventListener('change', this.onDocumentChange);

        // Intercept clicks on the dynamic-checkout guard (shown while no size is selected)
        // and surface the same "Please Choose Size" notice instead of letting checkout run.
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-dynamic-checkout-guard]')) {
            event.preventDefault();
            this.showSizeError();
          }
        });

        // Re-evaluate the gate after a variant re-render (e.g. a colour change can make the
        // previously selected size unavailable), once the new <variant-selects> is in place.
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            if (this.isConnected) this.updateSizeGate();
          });
        }

        this.updateSizeGate();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onDocumentChange);
        this.variantChangeUnsubscriber?.();
      }

      // Toggle the class that activates the dynamic-checkout guard overlay, and mark the
      // payment buttons `inert` so they cannot be reached/activated via keyboard either
      // (the overlay only catches pointer clicks). `inert` also drops an express-button
      // iframe from the tab order.
      updateSizeGate() {
        const unselected = this.isSizeUnselected();
        this.classList.toggle('size-required-unselected', unselected);
        const buttons = this.querySelector('[data-dynamic-checkout-buttons]');
        if (buttons) buttons.toggleAttribute('inert', unselected);
      }

      // The size fieldset within this product (scoped to its product-info so other
      // sections on the page are not affected). Null when the product has no size option.
      get sizeFieldset() {
        const root = this.closest('product-info') || document;
        return root.querySelector('fieldset.product-form__input--size-units');
      }

      isSizeUnselected() {
        const fieldset = this.sizeFieldset;
        if (!fieldset) return false;
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        if (!radios.length) return false;
        return !fieldset.querySelector('input[type="radio"]:checked');
      }

      showSizeError() {
        const fieldset = this.sizeFieldset;
        if (!fieldset) return;
        fieldset.classList.add('size-error');
        const notice = fieldset.querySelector('[data-size-notice]');
        if (notice) notice.hidden = false;
        fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      clearSizeError() {
        const fieldset = this.sizeFieldset;
        if (!fieldset) return;
        fieldset.classList.remove('size-error');
        const notice = fieldset.querySelector('[data-size-notice]');
        if (notice) notice.hidden = true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Require a size selection before adding to cart (size is not pre-selected on PDP load).
        if (this.isSizeUnselected()) {
          this.showSizeError();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status && response.status !== 422) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (response.status === 422) {
            const message = response.description || response.message;
            this.handleErrorMessage(message);

             // 🔥 Get latest cart state properly
            fetch(`${routes.cart_url}.js`)
            .then(res => res.json())
            .then(cart => {
            if (!cart.items.length) return;
            const lastItem = cart.items[cart.items.length - 1];
            const sections = this.cart.getSectionsToRender().map((section) => section.id);
            return fetch(`${routes.cart_url}?sections=${sections.join(',')}`)
            .then(res => res.json())
            .then(sectionData => {
              this.cart.renderContents({
              key: lastItem.key,
              sections: sectionData
            });

           });
          });

            this.error = false;
            return;
          }
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          clearTimeout(this.errorHideTimeout);
          this.errorHideTimeout = setTimeout(() => {
            this.errorMessageWrapper?.setAttribute('hidden', true);
          }, 6000);
        } else {
          clearTimeout(this.errorHideTimeout);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
